/**
 * Distributor Detail Component
 *
 * Read-only detail view for a single distributor.
 * Mirrors the OrganizationDetail look: back-button row with an Edit button,
 * a dark ProfileHeader hero, and dark ProfileSection cards.
 */

import { Link, useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ProfileHeader, ProfileSection } from '@/components/sensnet/ProfileLayout'
import { ArrowLeft, Loader2, Edit, Truck, Briefcase } from 'lucide-react'
import { useApiQuery } from '@/api/ApiQueryServiceProvider'

export interface DistributorDetailProps {
  distributorNumber: number
}

export function DistributorDetail({ distributorNumber }: DistributorDetailProps) {
  const navigate = useNavigate()

  const { data, isLoading } = useApiQuery<any>('getDeviceDistributor', { distributorNumber })
  const distributor = data?.content?.distributor

  const handleBack = () => navigate({ to: '/sensory/distributors' })

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading distributor details...</span>
        </div>
      </div>
    )
  }

  if (!distributor) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">Distributor not found.</p>
        <Button variant="outline" onClick={handleBack}>Back to Distributors</Button>
      </div>
    )
  }

  const isArchived = !!distributor.archiveDate

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={handleBack} className="gap-2 -ml-2">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Button asChild size="sm" className="gap-2">
          <Link
            to="/sensory/distributors/$distributorNumber/edit"
            params={{ distributorNumber: String(distributor.distributorNumber) }}
          >
            <Edit className="h-4 w-4" />
            Edit
          </Link>
        </Button>
      </div>

      <ProfileHeader
        title={distributor.distributorName}
        subtitle={`Distributor Number: ${distributor.distributorNumber}`}
        subtitleIcon={<Truck className="h-4 w-4" />}
        badge={isArchived ? 'Archived' : 'Active'}
      />

      <ProfileSection title="General Information" icon={<Briefcase className="h-4 w-4" />} variant="dark">
        <div className="space-y-3 p-5">
          <div>
            <p className="text-xs text-muted-foreground">Distributor Name</p>
            <p className="text-sm">{distributor.distributorName}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Distributor Number</p>
            <p className="text-sm">{distributor.distributorNumber}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Status</p>
            <p className="text-sm">{isArchived ? 'Archived' : 'Active'}</p>
          </div>
        </div>
      </ProfileSection>
    </div>
  )
}
